/**
 * Identifies the entity a case is ROOTED on — the asset the alert was raised
 * about — so the Visualizer can evaluate its blast radius.
 *
 * SCOPE. This answers "what is this case about", not "what happened". The
 * output seeds a radial blast-radius view; it is deliberately not an attack
 * flow or a timeline of events leading to the escalation.
 *
 * THE AFFECTED PARTY, NEVER THE ATTACKER. Every key below names the asset the
 * customer owns. That distinction is the whole reason this reads labelled
 * fields rather than scraping entity-shaped tokens out of prose: a description
 * mentions the attacker's address as readily as the victim's, and rooting a
 * blast radius on the attacker would invert the finding. Measured over 332,616
 * production tickets, a loose token scan "finds" an entity in 81.9% of cases
 * against 39.5% for these labelled fields — and most of that difference is
 * exactly the tokens we must not trust.
 *
 * TWISTER SEAM. Twister will emit a structured JSON object carrying the
 * affected asset explicitly. When it lands, `extractCaseOrigins` becomes a read
 * of that object and every pattern below is deleted — nothing else in the
 * feature changes, because callers only ever see `CaseOrigin`. Keep that
 * boundary intact: resolution, ranking and rendering must not learn to parse.
 */

import { SocAction, Ticket } from '../models/ticket.models';
import { KNOWN_KEYS } from './description-parser';
import { PivotNodeKind } from '../../inventory/pivot/models/pivot.models';

/** An entity a case names, before it has been matched to the inventory. */
export interface CaseOrigin {
  kind: Extract<PivotNodeKind, 'user' | 'endpoint' | 'application'>;
  /** Exactly as the ticket wrote it — what we search the inventory for. */
  name: string;
  /** The labelled field it came from, shown so a wrong root is explicable. */
  sourceField: string;
  /**
   * Ranking weight. Higher wins when a case names several things: a case is
   * rooted on ONE asset and the rest are context.
   */
  confidence: number;
}

const EMAIL = String.raw`[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}`;

/**
 * Where a field's value ends.
 *
 * Descriptions are frequently a single unbroken line, so a value cannot be
 * "everything to the newline" — that swallows every field after it. Measured:
 * bounding on the newline produced roots like
 * "user@example.com Source IP Address: 10.0.0.1 - Likely VPN = Yes Source IP
 * Location: …", which then failed the length check and took the case's coverage
 * down with it. Coverage read 15.5% instead of 39.5% for this reason alone.
 *
 * A value therefore ends at the next `Some Label:` — the same boundary the
 * description parser uses to separate keys — or at a newline, whichever comes
 * first.
 */
const VALUE_END = buildValueTerminator();

/**
 * Build the "value ends here" lookahead from the KNOWN keys.
 *
 * A generic `[A-Z][A-Za-z ...]{1,44}:` terminator is ambiguous against
 * multi-word values: in "App Display Name: Microsoft 365 Admin Portal Activity:
 * Consent" it fires at "Admin Portal Activity:" and truncates the app to
 * "Microsoft 365". Terminating on keys the description parser actually knows
 * resolves it — "Activity" is a known key, "Admin Portal Activity" is not.
 *
 * The generic form is kept as a fallback so an unlabelled ticket still bounds
 * somewhere, but it is tried second and capped at two words.
 */
function buildValueTerminator(): string {
  // Every key this file reads is also a key it must be able to stop at.
  const OWN_LABELS = [
    'Endpoint Name',
    'Username',
    'User ID/Display Name',
    'App Display Name',
    'Application',
    'Host Name',
    'Device Name',
    'Computer Name',
    'Machine Name',
    'Mailbox Owner',
    'User Name',
    'Primary Email Address',
    'User Principal Name',
    'Operation',
    'Alarm Type',
    'Risk Event Name',
    'Raw Parameters',
    'Object ID',
  ];
  const known = [...new Set([...KNOWN_KEYS, ...OWN_LABELS])]
    .sort((a, b) => b.length - a.length)
    .map((k) => k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .join('|');

  /**
   * The generic fallback is ONE capitalised word, deliberately.
   *
   * Allowing two made it fire before the known-key branch: a lookahead matches
   * at the earliest position any alternative can, so in "App Display Name:
   * Microsoft 365 Admin Portal Activity: Consent" a two-word form matched
   * "Portal Activity:" and truncated the app to "Microsoft 365 Admin". One word
   * can only match at "Activity:", which is where the value genuinely ends.
   */
  return String.raw`(?=\s+(?:${known})\s*:|\s+[A-Z][A-Za-z0-9'&.-]{1,24}\s*:|\n|$)`;
}

/** A value that is really an address, not the name of an asset. */
const ADDRESS_LIKE = /^(?:\d{1,3}(?:\.\d{1,3}){3}|[0-9a-f]{0,4}(?::[0-9a-f]{0,4}){2,}|https?:\/\/|www\.)/i;

/**
 * Labelled fields that name the affected asset, ordered by how directly they do
 * so. Yields are per 332,616 production tickets.
 */
const ORIGIN_FIELDS: {
  kind: CaseOrigin['kind'];
  field: string;
  pattern: RegExp;
  confidence: number;
}[] = [
  // Primary identifiers — the alert's own subject.
  {
    kind: 'endpoint',
    field: 'Endpoint Name',
    pattern: new RegExp(`Endpoint Name:\\s*(.+?)${VALUE_END}`, 'i'),
    confidence: 100,
  },
  { kind: 'user', field: 'Username', pattern: new RegExp(`Username:\\s*(.+?)${VALUE_END}`, 'i'), confidence: 95 },
  {
    kind: 'user',
    field: 'User ID/Display Name',
    pattern: new RegExp(`User ID/Display Name:\\s*(.+?)${VALUE_END}`, 'i'),
    confidence: 90,
  },
  {
    kind: 'application',
    field: 'App Display Name',
    pattern: new RegExp(`App Display Name:\\s*(.+?)${VALUE_END}`, 'i'),
    confidence: 85,
  },
  {
    kind: 'application',
    field: 'Application',
    pattern: new RegExp(`Application:\\s*(.+?)${VALUE_END}`, 'i'),
    confidence: 80,
  },

  // Secondary labels, added after measuring what the primaries miss. Together
  // these lift coverage from 35.0% to 39.5%; each still names the victim.
  /**
   * NOT preceded by "/ " or "Address ".
   *
   * `Hostname` also ends the compound key "Source IP Address / Hostname", which
   * on a phishing or connection alert carries the ATTACKER's infrastructure.
   * Without the guard this rooted a phishing case on
   * mail-relay.suspicious-domain.com — the sender's relay — and offered to
   * explore its blast radius as though it were an asset of ours. That is the
   * exact inversion this file exists to prevent.
   */
  {
    kind: 'endpoint',
    field: 'Host Name',
    pattern: new RegExp(`(?<!/ )(?<!Address )(?:Host|Device|Computer|Machine)\\s*Name:\\s*(.+?)${VALUE_END}`, 'i'),
    confidence: 75,
  }, // 7,994
  { kind: 'user', field: 'Mailbox Owner', pattern: new RegExp(`Mailbox Owner:\\s*(${EMAIL})`, 'i'), confidence: 75 }, // 2,584
  { kind: 'user', field: 'User Name', pattern: new RegExp(`User\\s*Name[^"]{0,20}"(${EMAIL})"`, 'i'), confidence: 70 }, // 2,307
  {
    kind: 'user',
    field: 'Primary Email Address',
    pattern: new RegExp(`Primary Email Address(?:\\s*\\(Receiver\\))?:\\s*(${EMAIL})`, 'i'),
    confidence: 70,
  }, // 1,412
  {
    kind: 'user',
    field: 'User Principal Name',
    pattern: new RegExp(`User Principal Name:\\s*(${EMAIL})`, 'i'),
    confidence: 70,
  }, // 460
];

/**
 * Trim a captured value back to the name itself.
 *
 * `VALUE_END` stops at the next labelled field, which handles run-on FIELDS but
 * not run-on PROSE — a description that continues "uspgcorp-dc01 (10.0.0.1)
 * logged in with …" has no next label to stop at. Names do not contain
 * parentheses, quotes or dashes-as-separators, so the first of those ends it.
 *
 * The token cap is 6, not 1: an application is legitimately "Microsoft 365
 * Admin Portal", so taking only the first word would truncate every multi-word
 * app in the inventory.
 */
function trimToName(value: string): string {
  const cut = value.split(/\s+[("']|\s+-\s+|["']\s|,\s/)[0] ?? value;
  const tokens = cut.trim().split(/\s+/);
  return tokens
    .slice(0, 6)
    .join(' ')
    .replace(/[.,;:)("']+$/, '')
    .trim();
}

/** Values a ticket writes when it has nothing to say. */
const EMPTY_VALUES = new Set(['', 'n/a', 'none', 'null', '-', 'unknown', 'not available']);

function usable(value: string): boolean {
  const v = value.trim();
  if (v.length < 2 || v.length > 128) return false;
  if (EMPTY_VALUES.has(v.toLowerCase())) return false;
  // An IPv4/IPv6 address or a URL is not the name of a tracked asset — and
  // inventory holds no addresses at all, so one could never resolve.
  if (ADDRESS_LIKE.test(v)) return false;
  return true;
}

/**
 * Domains that name nobody.
 *
 * Observed in the wild on ticket #385021, whose Recommended Remediation reads
 * "Reset the password for alice@example[.]com" while the summary names the real
 * account. RFC-2606 documentation domains and defanged (`[.]`) forms are
 * template filler or neutered indicators; either would resolve to nothing and
 * put a fabricated account at the centre of a blast radius.
 */
const PLACEHOLDER = /(?:^|@|\.)(?:example|test|invalid|localhost)\.(?:com|org|net|local)$|\[\.\]/i;

/**
 * Entities named by a SOC response action.
 *
 * A row reading "Session Revocation — dkoech@cyflare.com" states, in structured
 * form, who the SOC acted against. Several vendors (notably Stellar Cyber XDR)
 * put the account ONLY here: the Key Details block carries the source address
 * and the verdict, never the identity, so the labelled-field pass finds nothing
 * and the panel wrongly reported "this case does not name an account".
 *
 * This is not prose scraping. `parseSocActions` has already resolved these to
 * discrete rows, and only rows with a recognised action label are trusted —
 * section narrative scores `matchedLabel: false` and is ignored here exactly as
 * it is in the action list.
 */
function originsFromSocActions(actions: SocAction[]): CaseOrigin[] {
  const out: CaseOrigin[] = [];

  for (const action of actions) {
    if (!action.matchedLabel) continue;
    const entity = (action.hostname ?? '').trim();
    if (!entity || !usable(entity) || PLACEHOLDER.test(entity)) continue;

    // An action targets an account or a machine; the shape says which.
    const isEmail = new RegExp(`^${EMAIL}$`, 'i').test(entity);
    out.push({
      kind: isEmail ? 'user' : 'endpoint',
      name: entity,
      sourceField: `SOC action: ${action.label}`,
      // Below every explicitly labelled field — an action names the entity it
      // was aimed at, which is strong, but a Key Details label is canonical.
      confidence: isEmail ? 78 : 76,
    });
  }

  return out;
}

/**
 * Every asset this case names, best-rooted first.
 *
 * Returns all of them rather than only the winner: the strongest is the root of
 * the blast radius, and the others are what it is connected to WITHIN the case —
 * which is the smallest honest version of the map when nothing resolves.
 */
export function extractCaseOrigins(ticket: Ticket, socActions: SocAction[] = []): CaseOrigin[] {
  const text = ticket.description ?? '';
  const found = new Map<string, CaseOrigin>();

  const candidates: CaseOrigin[] = [];

  for (const { kind, field, pattern, confidence } of ORIGIN_FIELDS) {
    const match = pattern.exec(text);
    if (!match) continue;
    const name = trimToName(match[1]);
    if (!usable(name) || PLACEHOLDER.test(name)) continue;
    candidates.push({ kind, name, sourceField: field, confidence });
  }

  candidates.push(...originsFromSocActions(socActions));

  for (const candidate of candidates) {
    // One entity per name: several fields legitimately carry the same user, and
    // a repeated root would draw the same node twice.
    const key = `${candidate.kind}:${candidate.name.toLowerCase()}`;
    const existing = found.get(key);
    if (!existing || existing.confidence < candidate.confidence) {
      found.set(key, candidate);
    }
  }

  return [...found.values()].sort((a, b) => b.confidence - a.confidence);
}

/**
 * Why this case has no blast radius to show.
 *
 * Returned instead of a bare empty list because the reasons are different
 * findings. An alert about an IP pair names no asset BY NATURE — that is the
 * shape of a connection alert, not a gap in this case. An alert that should
 * name one and does not is a data-quality problem worth seeing.
 */
export type NoOriginReason = 'ip-only' | 'no-asset-named';

export function noOriginReason(ticket: Ticket): NoOriginReason {
  const hasIp = !!(ticket.source_ip_address_hostname_mac || ticket.destination_ip_address_hostname_mac);
  return hasIp ? 'ip-only' : 'no-asset-named';
}
